import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { articlesAPI } from '../services/api';
import ArticleCard from '../components/ArticleCard';
import { Link } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';

const HomePage = () => {
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [communityArticles, setCommunityArticles] = useState([]);
  const [sportsArticles, setSportsArticles] = useState([]);
  const [youthSportsArticles, setYouthSportsArticles] = useState([]);
  const [entertainmentArticles, setEntertainmentArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const all = await articlesAPI.getAll({ limit: 100 });
      populateState(all);
      setLoading(false);
    } catch (error) {
      console.error('Error loading articles:', error);
      setLoading(false);
    }
  };

  const populateState = (all) => {
      const featured = all.filter(a => a.featured).slice(0, 3);
      setFeaturedArticles(featured);
      const recent = all.slice(0, 12);
      setRecentArticles(recent);
      const sports = all.filter(a => a.category === 'sports').slice(0, 6);
      setSportsArticles(sports);
      const entertainment = all.filter(a => a.category === 'entertainment').slice(0, 6);
      setEntertainmentArticles(entertainment);
      const community = all.filter(a => ['community', 'education'].includes(a.category)).slice(0, 3);
      setCommunityArticles(community);
      const youthSports = all.filter(a => a.category === 'youth-sports').slice(0, 3);
      setYouthSportsArticles(youthSports);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <Loader2 className="animate-spin mx-auto mb-4" size={48} />
        <p className="text-gray-600">Loading articles...</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>The Central Coast Times | Conservative News & Tech Innovation</title>
        <meta name="description" content="Central Coast's conservative voice for tech innovation, business, and local news. Free markets, entrepreneurship, and the other side of the story." />
        <meta name="keywords" content="Central Coast news, conservative news, tech innovation, business news, NSW, local news, entrepreneurship, free markets" />
        <link rel="canonical" href="https://cctimes.au/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cctimes.au/" />
        <meta property="og:title" content="The Central Coast Times | Conservative News & Tech Innovation" />
        <meta property="og:description" content="Central Coast's voice for free markets, innovation, and the other side of the story." />
        <meta property="og:site_name" content="The Central Coast Times" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cctimes.au/" />
        <meta name="twitter:title" content="The Central Coast Times" />
        <meta name="twitter:description" content="Conservative news, tech innovation, and business from the Central Coast." />
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 py-6">

      {/* Hero Section - Dynamic Magazine Layout */}
      {featuredArticles.length > 0 && (
        <section className="mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Main Featured Story - Large */}
            {featuredArticles[0] && (
              <Link 
                to={`/article/${featuredArticles[0].slug}`}
                className="lg:row-span-2 relative group overflow-hidden rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <div className="aspect-[4/3] lg:aspect-[4/5] relative">
                  <img 
                    src={featuredArticles[0].image} 
                    alt={featuredArticles[0].title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=800&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8 text-white">
                    <span className="inline-block px-3 py-1 bg-red-600 text-xs font-bold uppercase tracking-wide mb-3">
                      {featuredArticles[0].category.replace('-', ' ')}
                    </span>
                    <h2 className="text-2xl lg:text-4xl font-serif font-bold mb-3 leading-tight">
                      {featuredArticles[0].title}
                    </h2>
                    <p className="text-gray-200 text-sm lg:text-base line-clamp-2">
                      {featuredArticles[0].excerpt}
                    </p>
                  </div>
                </div>
              </Link>
            )}

            {/* Side Featured Stories */}
            <div className="space-y-6">
              {featuredArticles.slice(1, 3).map((article, idx) => (
                <Link 
                  key={article._id || article.id || idx}
                  to={`/article/${article.slug}`}
                  className="block relative group overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="aspect-[16/9] relative">
                    <img 
                      src={article.image} 
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=800&q=80';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6 text-white">
                      <span className="inline-block px-2 py-1 bg-blue-600 text-xs font-bold uppercase tracking-wide mb-2">
                        {article.category.replace('-', ' ')}
                      </span>
                      <h3 className="text-lg lg:text-xl font-serif font-bold leading-tight line-clamp-2">
                        {article.title}
                      </h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Breaking News Ticker Style */}
      <div className="mb-10 border-t-2 border-b-2 border-red-600 py-3 bg-gray-50">
        <div className="flex items-center gap-4">
          <span className="bg-red-600 text-white px-4 py-1 font-bold text-sm uppercase tracking-wide">
            Latest
          </span>
          <div className="flex-1 overflow-hidden">
            <p className="text-sm font-semibold text-gray-900 animate-pulse">
              {recentArticles[0]?.title || "Stay tuned for breaking news and updates"}
            </p>
          </div>
        </div>
      </div>

      {/* Recent Articles - Dynamic Grid */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold border-l-4 border-red-600 pl-4">
            Latest Stories
          </h2>
          <Link to="/category/business" className="text-red-600 hover:text-red-700 font-semibold text-sm flex items-center gap-1">
            View All →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {recentArticles.map((article, idx) => (
            <Link
              key={article._id || article.id}
              to={`/article/${article.slug}`}
              className="group"
            >
              <div className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 mb-3">
                <div className="aspect-[4/3] relative">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=800&q=80';
                    }}
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-1 bg-black/70 text-white text-xs font-bold uppercase tracking-wide">
                      {article.category.replace('-', ' ')}
                    </span>
                  </div>
                </div>
              </div>
              <h3 className="text-lg font-serif font-bold mb-2 line-clamp-2 group-hover:text-red-600 transition-colors">
                {article.title}
              </h3>
              <p className="text-sm text-gray-600 line-clamp-2">
                {article.excerpt}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* Tech & Business Highlight Section */}
      {sportsArticles.length > 0 && (
        <section className="mb-12 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 lg:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-blue-900 border-l-4 border-blue-600 pl-4">
              Tech & Innovation
            </h2>
            <Link
              to="/category/sports"
              className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1"
            >
              More →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {sportsArticles.map((article) => (
              <Link
                key={article.id || article._id}
                to={`/article/${article.slug}`}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group"
              >
                <div className="aspect-[16/9] relative overflow-hidden">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=800&q=80';
                    }}
                  />
                </div>
                <div className="p-4">
                  <span className="text-xs font-bold text-blue-600 uppercase tracking-wide">
                    {article.category.replace('-', ' ')}
                  </span>
                  <h3 className="text-lg font-serif font-bold mt-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                    {article.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Community & Local Heroes */}
      {(communityArticles.length > 0 || youthSportsArticles.length > 0) && (
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-green-900 border-l-4 border-green-600 pl-4">
              Community Spotlight
            </h2>
            <Link
              to="/share-good-news"
              className="text-green-600 hover:text-green-700 font-semibold text-sm flex items-center gap-1"
            >
              Share Good News →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...communityArticles, ...youthSportsArticles].slice(0, 3).map((article) => (
              <Link
                key={article.id || article._id}
                to={`/article/${article.slug}`}
                className="group bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="aspect-[16/9] relative overflow-hidden">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=800&q=80';
                    }}
                  />
                </div>
                <div className="p-4">
                  <span className="text-xs font-bold text-green-600 uppercase tracking-wide">
                    {article.category.replace('-', ' ')}
                  </span>
                  <h3 className="text-lg font-serif font-bold mt-2 line-clamp-2 group-hover:text-green-600 transition-colors">
                    {article.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* More Featured Content - Grid Mix */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold border-l-4 border-purple-600 pl-4">
            Innovation & Business
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recentArticles.slice(4, 10).map((article) => (
            <Link
              key={article._id || article.id}
              to={`/article/${article.slug}`}
              className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
            >
              <div className="aspect-[16/9] relative overflow-hidden">
                <img 
                  src={article.image} 
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=800&q=80';
                  }}
                />
                <div className="absolute top-3 left-3">
                  <span className="px-2 py-1 bg-black/70 text-white text-xs font-bold uppercase tracking-wide">
                    {article.category.replace('-', ' ')}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-serif font-bold line-clamp-2 group-hover:text-purple-600 transition-colors">
                  {article.title}
                </h3>
                <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                  {article.excerpt}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Call to Action - Tech Innovation Focus */}
      <section className="mb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-600 to-indigo-800 text-white p-8 lg:p-10 shadow-xl">
            <div className="relative z-10">
              <h2 className="text-3xl font-serif font-bold mb-4">
                Share Innovation
              </h2>
              <p className="text-blue-100 mb-6 text-lg">
                Got a tech startup, innovation story, or business breakthrough? We want to hear about Central Coast's future.
              </p>
              <Link
                to="/submit-story"
                className="inline-block px-8 py-4 bg-white text-blue-600 rounded-lg hover:bg-gray-100 transition-colors font-bold text-lg"
              >
                Submit Your Story →
              </Link>
            </div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
          </div>

          <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-600 to-emerald-700 text-white p-8 lg:p-10 shadow-xl">
            <div className="relative z-10">
              <h2 className="text-3xl font-serif font-bold mb-4">
                Celebrate Progress
              </h2>
              <p className="text-green-100 mb-6 text-lg">
                Highlight success stories, entrepreneurial wins, and community achievements driving the Central Coast forward.
              </p>
              <Link
                to="/share-good-news"
                className="inline-block px-8 py-4 bg-white text-green-600 rounded-lg hover:bg-gray-100 transition-colors font-bold text-lg"
              >
                Share Success →
              </Link>
            </div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
          </div>
        </div>
      </section>

      {/* More Articles - Full Width Showcase */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold border-l-4 border-orange-600 pl-4">
            Trending Now
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...sportsArticles, ...entertainmentArticles].slice(0, 8).map((article) => (
            <Link
              key={article._id || article.id}
              to={`/article/${article.slug}`}
              className="group"
            >
              <div className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 mb-3">
                <div className="aspect-[4/3] relative">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=800&q=80';
                    }}
                  />
                </div>
              </div>
              <h3 className="text-base font-serif font-bold line-clamp-2 group-hover:text-orange-600 transition-colors">
                {article.title}
              </h3>
            </Link>
          ))}
        </div>
      </section>

      {/* Politics Section */}
      <section className="mb-12 bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-slate-900 border-l-4 border-slate-600 pl-4">
            Politics & Policy
          </h2>
          <Link
            to="/category/politics"
            className="text-slate-600 hover:text-slate-700 font-semibold text-sm flex items-center gap-1"
          >
            More →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {recentArticles.filter(a => a.category === 'politics').slice(0, 6).map((article) => (
            <Link
              key={article._id || article.id}
              to={`/article/${article.slug}`}
              className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group"
            >
              <div className="aspect-[16/9] relative overflow-hidden">
                <img 
                  src={article.image} 
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4">
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">
                  {article.category.replace('-', ' ')}
                </span>
                <h3 className="text-lg font-serif font-bold mt-2 line-clamp-2 group-hover:text-slate-600 transition-colors">
                  {article.title}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* More From Business */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold border-l-4 border-green-600 pl-4">
            Business & Economy
          </h2>
          <Link to="/category/business" className="text-green-600 hover:text-green-700 font-semibold text-sm flex items-center gap-1">
            View All →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recentArticles.filter(a => a.category === 'business').slice(0, 9).map((article) => (
            <Link
              key={article._id || article.id}
              to={`/article/${article.slug}`}
              className="group"
            >
              <div className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 mb-3">
                <div className="aspect-[16/9] relative">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=800&q=80';
                    }}
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-1 bg-black/70 text-white text-xs font-bold uppercase tracking-wide">
                      Business
                    </span>
                  </div>
                </div>
              </div>
              <h3 className="text-lg font-serif font-bold line-clamp-2 group-hover:text-green-600 transition-colors">
                {article.title}
              </h3>
              <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                {article.excerpt}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* More From Technology */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold border-l-4 border-indigo-600 pl-4">
            Latest in Technology
          </h2>
          <Link to="/category/technology" className="text-indigo-600 hover:text-indigo-700 font-semibold text-sm flex items-center gap-1">
            View All →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {recentArticles.filter(a => a.category === 'technology').slice(0, 8).map((article) => (
            <Link
              key={article._id || article.id}
              to={`/article/${article.slug}`}
              className="group"
            >
              <div className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 mb-3">
                <div className="aspect-[4/3] relative">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=800&q=80';
                    }}
                  />
                </div>
              </div>
              <h3 className="text-base font-serif font-bold line-clamp-2 group-hover:text-indigo-600 transition-colors">
                {article.title}
              </h3>
            </Link>
          ))}
        </div>
      </section>
      </div>
    </>
  );
};

export default HomePage;